import { createRouter, createWebHistory } from 'vue-router';
import { defineAsyncComponent, h } from 'vue';

import Swal from 'sweetalert2';
import i18n from '@/api/i18n';
import { clearLoading, setNavigationLoading } from '@/plugins/loading';
import { resolveRouteLocale } from '@/utils/locale';

const createAsyncView = loader =>
    defineAsyncComponent({
        loader,
        delay: 200,
        timeout: 15000,
        suspensible: false,
        errorComponent: {
            name: 'RouteLoadError',
            setup() {
                return () =>
                    h('div', { class: 'container py-5 text-center' }, [
                        h('h4', { class: 'mb-2' }, 'Unable to load this page'),
                        h(
                            'p',
                            { class: 'text-muted' },
                            'Please refresh and try again.'
                        ),
                    ]);
            },
        },
        onError(error, retry, fail, attempts) {
            const message = String(error?.message || '').toLowerCase();
            const isTransientError =
                message.includes('fetch') ||
                message.includes('import') ||
                message.includes('chunk');

            if (isTransientError && attempts <= 3) {
                retry();
                return;
            }

            console.error('Failed to load route component:', error);
            fail(error);
        },
    });

const MainLayout = createAsyncView(() => import('@/views/MainLayout.vue'));
const HomeView = createAsyncView(() => import('@/views/HomeView.vue'));
const DemoStatsAndCharts = createAsyncView(() =>
    import('@/views/DemoStatsAndCharts.vue')
);
const DiagnosisBatteryTest = createAsyncView(() =>
    import('@/views/DiagnosisBatteryTest.vue')
);
const DiagnosisCheckCable = createAsyncView(() =>
    import('@/views/DiagnosisCheckCable.vue')
);
const Users = createAsyncView(() => import('@/views/UsersList.vue'));
const UserEdit = createAsyncView(() => import('@/views/UserEdit.vue'));
const UserCreate = createAsyncView(() => import('@/views/UserCreate.vue'));
const EmailBlacklist = createAsyncView(() =>
    import('@/views/EmailBlacklist.vue')
);
const SigninView = createAsyncView(() => import('@/views/SigninView.vue'));
const ForgotPassword = createAsyncView(() =>
    import('@/views/ForgotPassword.vue')
);
const UserVerification = createAsyncView(() =>
    import('@/views/UserVerification.vue')
);
const ResetPasswordVerification = createAsyncView(() =>
    import('@/views/ResetPasswordVerification.vue')
);
const VerifyUser = createAsyncView(() => import('@/views/VerifyUser.vue'));
const ResetPassword = createAsyncView(() =>
    import('@/views/ResetPassword.vue')
);
const ChangePassword = createAsyncView(() =>
    import('@/views/ChangePassword.vue')
);
const PrivacyPolicy = createAsyncView(() =>
    import('@/views/PrivacyPolicy.vue')
);
const UserBatchCreate = createAsyncView(() =>
    import('@/views/UserBatchCreate.vue')
);
const UserBatchDelete = createAsyncView(() =>
    import('@/views/UserBatchDelete.vue')
);
const TestReport = createAsyncView(() => import('@/views/TestReport.vue'));
const CableDiagnosis = createAsyncView(() =>
    import('@/views/CableDiagnosis.vue')
);
const ReportSharePage = createAsyncView(() =>
    import('@/views/ReportSharePage.vue')
);
const ShopManagement = createAsyncView(() =>
    import('@/views/ShopManagement.vue')
);
const DiagnosisDualBatteryTest = createAsyncView(() =>
    import('@/views/DiagnosisDualBatteryTest.vue')
);
const IOTTestRecords = createAsyncView(() =>
    import('@/views/IOTTestRecords.vue')
);
const DeviceManagement = createAsyncView(() =>
    import('@/views/DeviceManagement.vue')
);
const RegisterView = createAsyncView(() => import('@/views/RegisterView.vue'));
const ClubInvitation = createAsyncView(() =>
    import('@/views/ClubInvitation.vue')
);
const AdvancedDashboard = createAsyncView(() =>
    import('@/views/AdvancedDashboard.vue')
);
const TenantLogoUpload = createAsyncView(() =>
    import('@/views/TenantLogoUpload.vue')
);

import store from '@/store/index.js';

const t = i18n.global.t;

const routes = [
    {
        path: '/integrations/authorize',
        name: 'IntegrationAuthorize',
        component: () => import('@/views/IntegrationAuthorize.vue'),
    },
    {
        path: '/',
        name: 'signin',
        component: SigninView,
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: ForgotPassword,
        props: true,
    },
    {
        path: '/verify-user-token/:token',
        name: 'verify-user-token',
        component: UserVerification,
        props: true,
    },
    {
        path: '/verify-user/:token',
        name: 'verify-user',
        component: VerifyUser,
        props: true,
    },
    {
        path: '/verify-reset-password-token/:token',
        name: 'verify-reset-password-token',
        component: ResetPasswordVerification,
        props: true,
    },
    {
        path: '/club-invitation/:token',
        name: 'club-invitation',
        component: ClubInvitation,
        props: true,
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: ResetPassword,
        props: true,
    },
    {
        path: '/dashboard',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'home',
                name: 'Home',
                component: HomeView,
            },
            {
                path: 'demo-stats-charts',
                name: 'DemoStatsAndCharts',
                component: DemoStatsAndCharts,
                props: route => ({
                    roleView: route.query.role || null,
                }),
            },
            {
                path: 'advanced',
                name: 'AdvancedDashboard',
                component: AdvancedDashboard,
            },
        ],
    },
    {
        path: '/users',
        name: 'Users',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'list',
                name: 'List all users',
                component: Users,
            },
            {
                path: 'create',
                name: 'Create a user',
                component: UserCreate,
            },
            {
                path: 'create-employee',
                name: 'Create an employee user',
                component: UserCreate,
                props: { userAccountLevel: 'Employee' },
                key: 'create-employee',
            },
            {
                path: 'create-business',
                name: 'Create a business user',
                component: UserCreate,
                props: { userAccountLevel: 'Business' },
                key: 'create-business',
            },
            {
                path: 'edit/:id',
                name: 'Edit user',
                component: UserEdit,
            },
            {
                path: 'change-password',
                name: 'Change password',
                component: ChangePassword,
            },
            {
                path: 'batch-create',
                name: 'Batch create users',
                component: UserBatchCreate,
            },
            {
                path: 'batch-delete',
                name: 'Batch delete users',
                component: UserBatchDelete,
            },
        ],
    },
    {
        path: '/diagnosis',
        name: 'Diagnosis',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'battery-test',
                name: 'Battery Test',
                component: DiagnosisBatteryTest,
            },
            {
                path: 'check-cable',
                name: 'Check Cable',
                component: DiagnosisCheckCable,
            },
            {
                path: 'dual-battery-test',
                name: 'Dual Battery Test',
                component: DiagnosisDualBatteryTest,
            },
        ],
    },
    {
        path: '/configs',
        name: 'Configs',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'email-blacklist',
                name: 'Email blacklist',
                component: EmailBlacklist,
            },
            {
                path: 'tenant-logo-upload',
                name: 'Tenant Logo Upload',
                component: TenantLogoUpload,
            },
        ],
    },
    {
        path: '/shopManagement',
        name: 'Shop Management',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'list',
                name: 'List all shops',
                component: ShopManagement,
            },
        ],
    },
    {
        path: '/sign-in/change-password',
        name: 'Signin and change password',
        component: ChangePassword,
        props: { formTitle: 'login.login_msg__account_default_password' },
    },
    {
        path: '/sign-in/change-expired-password',
        name: 'Signin and change expired password',
        component: ChangePassword,
    },
    {
        path: '/privacy-policy',
        name: 'Privacy Policy',
        component: PrivacyPolicy,
        meta: { requiresAuth: false },
    },
    {
        path: '/test-report/:uuid/:integrationKey/:clubId',
        name: 'Test Report',
        component: TestReport,
        meta: { requiresAuth: false },
        props: true,
    },
    {
        path: '/cable-diagnosis/:uuid/:integrationKey/:clubId',
        name: 'Cable Diagnosis',
        component: CableDiagnosis,
        meta: { requiresAuth: false },
        props: true,
    },
    {
        path: '/reports/share/:token',
        name: 'Report Share',
        component: ReportSharePage,
        meta: { requiresAuth: false },
        props: true,
    },
    {
        path: '/iot',
        name: 'IoT',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            // index (supports query: ?device_model=xxx&test_type=yyy)
            {
                path: '',
                name: 'IOTTestRecords',
                component: IOTTestRecords,
            },
            // /iot/:device_model
            {
                path: ':device_model',
                name: 'IOTTestRecordsByModel',
                component: IOTTestRecords,
            },
            // /iot/:device_model/:test_type
            {
                path: ':device_model/:test_type',
                name: 'IOTTestRecordsByModelAndType',
                component: IOTTestRecords,
            },
            {
                path: 'deviceManagement',
                name: 'Device Management',
                component: DeviceManagement,
            },
        ],
    },
    {
        path: '/register',
        name: 'register',
        component: RegisterView,
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

router.beforeEach(async (to, from, next) => {
    setNavigationLoading(true);

    try {
        await store.dispatch(
            'i18n/ensureLocaleLoaded',
            resolveRouteLocale(to.query) || undefined
        );
    } catch (error) {
        console.error('Failed to load locale messages:', error);
    }

    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!store.getters['auth/isAuthenticated']) {
            clearLoading();
            const result = await Swal.fire({
                title: t('django.system_msg__status_code_401'),
                text: 'You need to sign in to access this page',
                icon: 'warning',
                confirmButtonText: 'OK',
            });

            if (result.isConfirmed) {
                next({ path: '/' });
            } else {
                setNavigationLoading(false);
                next(false);
            }
            return;
        }
    }

    next();
});

router.afterEach(() => {
    setNavigationLoading(false);
});

router.onError(error => {
    console.error('Router navigation error:', error);
    setNavigationLoading(false);
});

export default router;
